import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import "./District.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditDistrict = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [data, setData] = useState({
    name: "",
    country: "",
    region: "",
  });

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);

  // Fetch Countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${url}/api/country`);
        setCountries(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load countries");
      }
    };

    fetchCountries();
  }, []);

  // Fetch District Details
  useEffect(() => {
    const fetchDistrict = async () => {
      try {
        const res = await axios.get(`${url}/api/district/${id}`);
        const district = res.data.data;

        setData({
          name: district.name,
          country: district.country?._id || "",
          region: district.region?._id || "",
        });
      } catch (error) {
        toast.error("Failed to load district");
        navigate("/ViewDistrict");
      } finally {
        setLoading(false);
      }
    };

    fetchDistrict();
  }, [id, navigate]);

  // Fetch Regions when country changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (!data.country) {
        setRegions([]);
        return;
      }

      try {
        const res = await axios.get(`${url}/api/Regions?country=${data.country}`);
        setRegions(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load regions");
      }
    };

    fetchRegions();
  }, [data.country]);

  const onChangeHandler = (event) => {
    const { name, value } = event.target;

    setData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === "country" ? { region: "" } : {}),
    }));
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    if (!data.name || !data.country || !data.region) {
      toast.error("All fields are required");
      return;
    }

    try {
      setSubmitting(true);

      const res = await axios.put(`${url}/api/district/${id}`, data);

      if (res.data.success) {
        toast.success("District updated successfully");
        navigate("/ViewDistrict");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update district");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading district...</div>;

  return (
    <div className="district-container">
      <div className="header">
        <h2>Edit District</h2>

        <button
          onClick={() => navigate("/ViewDistrict")}
          className="add-button"
        >
          Back to List
        </button>
      </div>

      <form className="district-form" onSubmit={onSubmitHandler}>
        <div className="form-group">
          <label>District Name</label>
          <input
            type="text"
            name="name"
            value={data.name}
            onChange={onChangeHandler}
            required
          />
        </div>

        <div className="form-group">
          <label>Country</label>
          <select
            name="country"
            value={data.country}
            onChange={onChangeHandler}
            required
          >
            <option value="">Select Country</option>

            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Region</label>

          <select
            name="region"
            value={data.region}
            onChange={onChangeHandler}
            required
            disabled={!data.country}
          >
            <option value="">Select Region</option>

            {regions.map((region) => (
              <option key={region._id} value={region._id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update District"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/ViewDistrict")}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDistrict;